import { Col, Form, Input } from "antd";
import { FormItemProps } from "antd/lib/form";
import debounce from "lodash/debounce";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FieldConstraintViolation } from "../../../../common/types";
import { ClientFormStage, ClientSearchActionType } from "../../enums";
import { Client } from "../../types";
import { ClientTypeTagWithName } from "../tags/ClientTypeTagWithName";
import ClientActionsFormLabel from "./ClientActionsFormLabel";

export interface ClientSearchInputProps<Type> {
  formItemProps: FormItemProps;
  formStage?: ClientFormStage;
  formType: Type;
  processedType?: Type;
  client?: Partial<Client>;
  violationErrors: Map<Type, FieldConstraintViolation[]>;
  inProgress: boolean;
  disabled?: boolean;
  optional?: boolean;
  inputColSpan?: number;
  clientNameColSpan?: number;
  onActionClick: (formType: Type, actionType: ClientSearchActionType) => void;
  onFocus?: (formType: Type) => void;
  onSearch: (value: string, formType: Type) => void;
  onChange: (value: string, formType: Type) => void;
}

const DEFAULT_COL_SPAN = 8;

const ClientSearchInput = <Type,>({ formType, ...props }: ClientSearchInputProps<Type>) => {
  const { t } = useTranslation();

  let errorStatus: Record<"help" | "validateStatus", any> = { help: undefined, validateStatus: undefined };

  if (props.violationErrors.has(formType)) {
    errorStatus = { help: t("client.helpers.search.clientsDataError"), validateStatus: "error" };
  } else if (props.formStage === ClientFormStage.NEW) {
    errorStatus = { help: t("client.helpers.search.clientsDataMissingError"), validateStatus: "warning" };
  }

  const handleChangeClientSearch = useMemo(() => debounce(props.onChange, 1000), []);

  return (
    <>
      <Col span={props.inputColSpan || DEFAULT_COL_SPAN}>
        <Form.Item
          {...errorStatus}
          name={props.formItemProps.name}
          required={false}
          rules={props.formItemProps.rules}
          initialValue={props.formItemProps.initialValue}
          label={
            <ClientActionsFormLabel
              label={props.formItemProps.label}
              stage={props.formStage}
              optional={props.optional}
              onCreateClick={() => props.onActionClick(formType, ClientSearchActionType.CREATE)}
              onUpdateClick={() => props.onActionClick(formType, ClientSearchActionType.UPDATE)}
              onDeleteClick={
                !props.disabled ? () => props.onActionClick(formType, ClientSearchActionType.DELETE) : undefined
              }
            />
          }
        >
          <Input.Search
            disabled={props.disabled || props.formStage === ClientFormStage.SELECTED}
            loading={props.inProgress && props.processedType === formType}
            onFocus={() => props.onFocus?.(formType)}
            onSearch={(value) => props.onSearch(value, formType)}
            onChange={(event) => {
              props.onActionClick(formType, ClientSearchActionType.CLEAR);
              handleChangeClientSearch(event.target.value, formType);
            }}
          />
        </Form.Item>
      </Col>
      <Col
        span={props.clientNameColSpan || DEFAULT_COL_SPAN}
        style={{ height: 32, display: "flex", alignItems: "center" }}
        className="form-item-without-label"
      >
        {props.client && <ClientTypeTagWithName client={props.client} />}
      </Col>
    </>
  );
};

export default ClientSearchInput;
